/**
 * Ansicht eines Träger-Users in dieser Sitzung: Träger-Bereich, eigene Schule oder eine
 * freigegebene Schule des Trägers. Liegt im sessionStorage, damit jeder Tab für sich
 * wechseln kann. Ist der Speicher gesperrt, bleibt es bei der Standardansicht.
 *
 * Die gewählte fremde Schule geht als Header X-Schule-Id an das Backend
 * (tenantMiddleware prüft dort die Freigabe).
 */
const KEY = "mediskill.ansicht";

export type CarrierView =
  | { mode: "traeger" }
  | { mode: "eigene" }
  | { mode: "schule"; schoolId: number };

function read(): string | null {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

function write(value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(KEY);
    else sessionStorage.setItem(KEY, value);
  } catch { /* ohne Speicher gilt die Standardansicht */ }
}

/** Gespeicherte Ansicht, oder null, wenn in dieser Sitzung noch nichts gewählt wurde. */
export function getCarrierView(): CarrierView | null {
  const raw = read();
  if (raw === "traeger") return { mode: "traeger" };
  if (raw === "eigene") return { mode: "eigene" };
  const id = raw?.startsWith("schule:") ? Number(raw.slice(7)) : NaN;
  if (Number.isInteger(id) && id > 0) return { mode: "schule", schoolId: id };
  return null;
}

/** ID der fremden Schule für den Header X-Schule-Id, sonst null. */
export function getSchoolHeader(): string | null {
  const view = getCarrierView();
  return view?.mode === "schule" ? String(view.schoolId) : null;
}

export function setCarrierView(view: CarrierView | null): void {
  if (!view) write(null);
  else if (view.mode === "schule") write(`schule:${view.schoolId}`);
  else write(view.mode);
}

/** Ansicht wechseln und die App neu starten, damit Menü und Rechte neu geladen werden. */
export function switchCarrierView(view: CarrierView, hash: string): void {
  setCarrierView(view);
  // replaceState statt location.hash: kein hashchange, sonst liefe navigate() noch in der alten Ansicht
  history.replaceState(null, "", hash);
  window.location.reload();
}

/** Schule aus dem Träger-Bereich öffnen: die eigene ohne Header, jede andere per X-Schule-Id. */
export function openCarrierSchool(schoolId: number, homeSchoolId: number | null | undefined): void {
  switchCarrierView(schoolId === homeSchoolId ? { mode: "eigene" } : { mode: "schule", schoolId }, "#/dashboard");
}
